import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import LessonBlock from "../components/LessonBlock";
import PayPalCheckoutDialog from "../components/PayPalCheckoutDialog";
import { PlaybookDialog, SectionDialog, LessonDialog } from "../components/PlaybookEditorDialogs";
import { ArrowLeft, Plus, Pencil, Trash2, Layers, GraduationCap, ChevronRight, ChevronLeft, EyeOff, Lock, Settings, Check, CheckCircle2, MessageSquare, ChevronUp, ChevronDown, Users, BookOpen, Menu } from "lucide-react";
import { toast } from "sonner";

export default function PlaybookDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "moderator";
  const isAdmin = user?.role === "admin";

  const [playbook, setPlaybook] = useState(null);
  const [sections, setSections] = useState([]);
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completedIds, setCompletedIds] = useState(() => new Set());
  const [completionCounts, setCompletionCounts] = useState({});
  const [mobileSyllabusOpen, setMobileSyllabusOpen] = useState(false);
  const [busyLesson, setBusyLesson] = useState(null); // lesson id currently toggling / spawning
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradePlaybookOnly, setUpgradePlaybookOnly] = useState(false);
  const [liveViewers, setLiveViewers] = useState(0);

  // Dialogs
  const [editPlaybookOpen, setEditPlaybookOpen] = useState(false);
  const [deletePlaybookOpen, setDeletePlaybookOpen] = useState(false);
  const [sectionDialog, setSectionDialog] = useState({ open: false, section: null });
  const [lessonDialog, setLessonDialog] = useState({ open: false, sectionId: null, lesson: null });
  const [deleteTarget, setDeleteTarget] = useState(null); // {type: "section"|"lesson", id, title}

  const load = useCallback(async () => {    setLoading(true);
    try {
      const [detailRes, progRes, statsRes] = await Promise.all([
        api.get(`/playbooks/${id}`),
        api.get(`/playbooks/${id}/progress`).catch(() => ({ data: { completed_lesson_ids: [] } })),
        api.get(`/playbooks/${id}/stats`).catch(() => ({ data: { completion_counts: {} } })),
      ]);
      setPlaybook(detailRes.data.playbook);
      setSections(detailRes.data.sections);
      setCompletedIds(new Set(progRes.data.completed_lesson_ids || []));
      setCompletionCounts(statsRes.data.completion_counts || {});
      // If ?lesson=<id> is in the URL (e.g. from a Community back-link), open it.
      const requested = searchParams.get("lesson");
      if (requested) {
        const found = detailRes.data.sections.flatMap((s) => s.lessons).find((l) => l.id === requested);
        if (found) {
          setActiveLessonId(found.id);
          setSearchParams({}, { replace: true });
          return;
        }
      }
      if (!activeLessonId) {
        const firstLesson = detailRes.data.sections.flatMap((s) => s.lessons)[0];
        if (firstLesson) setActiveLessonId(firstLesson.id);
      }
    } catch (err) {
      toast.error(formatApiError(err));
      if (err?.response?.status === 404) navigate("/playbook", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [id, navigate, activeLessonId, searchParams, setSearchParams]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Presence: heartbeat "I'm viewing this playbook" every 45s + poll live count every 30s.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const ping = () => api.post("/presence/viewing", { kind: "playbook", id }).catch(() => {});
    const fetchCount = async () => {
      try {
        const { data } = await api.get(`/playbooks/${id}/live`);
        if (!cancelled) setLiveViewers(Number(data?.viewers || 0));
      } catch (err) { console.debug("Playbook live viewer fetch failed:", err?.message || err); }
    };
    ping();
    fetchCount();
    const t1 = setInterval(ping, 45_000);
    const t2 = setInterval(fetchCount, 30_000);
    return () => { cancelled = true; clearInterval(t1); clearInterval(t2); };
  }, [id]);

  const activeLesson = useMemo(() => {
    for (const s of sections) {
      const found = s.lessons.find((l) => l.id === activeLessonId);
      if (found) return { section: s, lesson: found };
    }
    return null;
  }, [sections, activeLessonId]);

  const totalLessons = useMemo(() => sections.reduce((n, s) => n + s.lessons.length, 0), [sections]);
  const completedCount = completedIds.size;
  const progressPct = totalLessons > 0 ? Math.round((completedCount / totalLessons) * 100) : 0;

  // Flat list of every lesson in sidebar order — powers Prev/Next + auto-advance.
  const flatLessons = useMemo(() => sections.flatMap((s) => s.lessons), [sections]);
  const activeIdx = useMemo(
    () => flatLessons.findIndex((l) => l.id === activeLessonId),
    [flatLessons, activeLessonId]
  );
  const prevLesson = activeIdx > 0 ? flatLessons[activeIdx - 1] : null;
  const nextLesson = activeIdx >= 0 && activeIdx < flatLessons.length - 1 ? flatLessons[activeIdx + 1] : null;

  const toggleComplete = async (lessonId) => {
    if (busyLesson) return;
    setBusyLesson(lessonId);
    const wasDone = completedIds.has(lessonId);
    // Optimistic update
    setCompletedIds((prev) => {
      const next = new Set(prev);
      if (wasDone) next.delete(lessonId); else next.add(lessonId);
      return next;
    });
    try {
      if (wasDone) await api.delete(`/playbook-lessons/${lessonId}/complete`);
      else await api.post(`/playbook-lessons/${lessonId}/complete`);
      // Auto-advance: only when marking complete (not un-completing) and the
      // lesson toggled matches the currently viewed one — keeps the learner
      // in flow. Skip if there's no next lesson.
      if (!wasDone && lessonId === activeLessonId) {
        const idx = flatLessons.findIndex((l) => l.id === lessonId);
        const nxt = idx >= 0 ? flatLessons[idx + 1] : null;
        if (nxt) setActiveLessonId(nxt.id);
      }
    } catch (err) {
      // Roll back on failure
      setCompletedIds((prev) => {
        const next = new Set(prev);
        if (wasDone) next.add(lessonId); else next.delete(lessonId);
        return next;
      });
      toast.error(formatApiError(err));
    } finally {
      setBusyLesson(null);
    }
  };

  const openDiscussion = async (lessonId) => {
    if (busyLesson) return;
    setBusyLesson(lessonId);
    try {
      const { data } = await api.post(`/playbook-lessons/${lessonId}/discuss`);
      if (data.created) toast.success("Discussion thread started");
      navigate(`/community/${data.topic_id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyLesson(null);
    }
  };

  const reorder = async (kind, targetId, direction) => {
    try {
      const url = kind === "section"
        ? `/playbook-sections/${targetId}/reorder`
        : `/playbook-lessons/${targetId}/reorder`;
      await api.patch(url, { direction });
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const savePlaybook = async (payload) => {
    try {
      await api.patch(`/playbooks/${id}`, payload);
      toast.success("Playbook saved");
      setEditPlaybookOpen(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const doDeletePlaybook = async () => {
    try {
      await api.delete(`/playbooks/${id}`);
      toast.success("Playbook deleted");
      navigate("/playbook");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const saveSection = async (payload) => {
    try {
      if (sectionDialog.section) {
        await api.patch(`/playbook-sections/${sectionDialog.section.id}`, payload);
        toast.success("Section saved");
      } else {
        await api.post(`/playbooks/${id}/sections`, payload);
        toast.success("Section added");
      }
      setSectionDialog({ open: false, section: null });
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const saveLesson = async (payload) => {
    try {
      if (lessonDialog.lesson) {
        await api.patch(`/playbook-lessons/${lessonDialog.lesson.id}`, payload);
        toast.success("Lesson saved");
      } else {
        const { data } = await api.post(`/playbook-sections/${lessonDialog.sectionId}/lessons`, payload);
        setActiveLessonId(data.id);
        toast.success("Lesson added");
      }
      setLessonDialog({ open: false, sectionId: null, lesson: null });
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === "section") {
        await api.delete(`/playbook-sections/${deleteTarget.id}`);
        toast.success("Section deleted");
      } else if (deleteTarget.type === "lesson") {
        await api.delete(`/playbook-lessons/${deleteTarget.id}`);
        toast.success("Lesson deleted");
        if (activeLessonId === deleteTarget.id) setActiveLessonId(null);
      }
      setDeleteTarget(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="max-w-6xl mx-auto py-8 px-4 sm:px-6">
          <div className="text-slate-500 font-mono-num text-sm">Loading…</div>
        </div>
      </AppLayout>
    );
  }
  if (!playbook) return null;

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto py-6 px-4 sm:px-6" data-testid="playbook-detail">
        {playbook.cover_image_url && (
          <div
            className="mb-6 h-40 sm:h-48 border border-slate-200 overflow-hidden flex items-center justify-center"
            style={{ backgroundColor: playbook.cover_color || "#f8fafc" }}
            data-testid="playbook-cover-container"
          >
            <img src={playbook.cover_image_url} alt="" className="w-full h-full object-contain" data-testid="playbook-cover-image" />
          </div>
        )}
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="min-w-0">
            <Link to="/playbook" className="inline-flex items-center gap-1.5 font-mono-num text-[10px] uppercase tracking-widest text-slate-500 hover:text-[#0D9488] mb-2" data-testid="playbook-back">
              <ArrowLeft className="w-3 h-3" /> All Playbooks
            </Link>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="w-1.5 h-8" style={{ backgroundColor: playbook.cover_color || "#0D9488" }} />
              <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-slate-900" data-testid="playbook-title">
                {playbook.title}
              </h1>
              {!playbook.published && (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono-num uppercase tracking-widest text-[#0D9488] border border-[#0D9488]/40 px-2 py-0.5">
                  <EyeOff className="w-2.5 h-2.5" /> Draft
                </span>
              )}
              {playbook.admin_only && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-mono-num uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-300 px-2 py-0.5"
                  data-testid="playbook-admin-only-badge"
                >
                  <Lock className="w-2.5 h-2.5" /> Admin only
                </span>
              )}
            </div>
            {playbook.description && (
              <p className="text-slate-600 mt-2 max-w-2xl text-sm leading-relaxed">{playbook.description}</p>
            )}
            <div className="mt-3 flex items-center gap-4 font-mono-num text-[10px] uppercase tracking-widest text-slate-500 flex-wrap">
              <span className="inline-flex items-center gap-1"><Layers className="w-3 h-3 text-[#0D9488]" /> {playbook.section_count} sections</span>
              <span className="inline-flex items-center gap-1"><GraduationCap className="w-3 h-3 text-[#0D9488]" /> {playbook.lesson_count} lessons</span>
              {playbook.created_by_name && <span>by {playbook.created_by_name}</span>}
              {liveViewers > 0 && (
                <span
                  className="inline-flex items-center gap-1.5 border border-emerald-500/40 bg-emerald-500/[0.06] text-emerald-400 px-2 py-0.5"
                  title={`${liveViewers} member${liveViewers === 1 ? "" : "s"} reading right now`}
                  data-testid="playbook-live-viewers"
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                  </span>
                  <span className="tabular-nums">{liveViewers}</span> reading now
                </span>
              )}
            </div>
            {totalLessons > 0 && (
              <div className="mt-3" data-testid="playbook-progress">
                <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-1.5">
                  <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#0D9488]" /> Your Progress</span>
                  <span className="tabular-nums text-slate-500 font-mono-num">{completedCount} of {totalLessons} · {progressPct}%</span>
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#0D9488] transition-all duration-500 rounded-full" style={{ width: `${progressPct}%` }} />
                </div>
              </div>
            )}
          </div>
          {isStaff && (
            <div className="flex gap-2 shrink-0">
              <Button onClick={() => setEditPlaybookOpen(true)} variant="outline"
                className="border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-800"
                data-testid="playbook-edit">
                <Settings className="w-3.5 h-3.5 mr-2" /> Settings
              </Button>
              {isAdmin && (
                <Button onClick={() => setDeletePlaybookOpen(true)} variant="outline"
                  className="border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-600 hover:text-[#FF3B30]"
                  data-testid="playbook-delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
          {/* Mobile-only Syllabus trigger — opens the drawer with the same
              module → lesson list as the desktop sidebar. Keeps the reading
              area distraction-free once a lesson is selected. */}
          <button
            type="button"
            onClick={() => setMobileSyllabusOpen(true)}
            className="lg:hidden w-full flex items-center justify-between gap-2 border border-slate-200 bg-white hover:border-teal-500 rounded-lg px-4 py-3 mb-1"
            data-testid="playbook-syllabus-trigger"
          >
            <span className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-[#0D9488]" />
              <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-700">Syllabus</span>
              {totalLessons > 0 && (
                <span className="font-mono-num text-[10px] text-slate-500 tabular-nums ml-1">
                  {completedCount}/{totalLessons}
                </span>
              )}
            </span>
            <span className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600">
              Modules <Menu className="w-3.5 h-3.5" />
            </span>
          </button>

          <Sheet open={mobileSyllabusOpen} onOpenChange={setMobileSyllabusOpen}>
            <SheetContent side="left" className="w-full sm:max-w-sm p-0 bg-white overflow-y-auto" data-testid="playbook-syllabus-drawer">
              <SheetHeader className="px-5 py-4 border-b border-slate-200">
                <SheetTitle className="text-slate-900 text-lg font-bold flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-[#0D9488]" /> Syllabus
                </SheetTitle>
              </SheetHeader>
              <div className="p-4">
                {sections.map((sec) => (
                  <div key={`m-${sec.id}`} className="mb-4">
                    <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 px-1 mb-1.5">
                      {sec.title}
                    </div>
                    <div>
                      {(sec.lessons || []).map((lesson) => {
                        const isActive = activeLessonId === lesson.id;
                        const isDone = completedIds.has(lesson.id);
                        return (
                          <button
                            key={`ml-${lesson.id}`}
                            type="button"
                            onClick={() => { setActiveLessonId(lesson.id); setMobileSyllabusOpen(false); }}
                            className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-md text-left transition-colors ${isActive ? "bg-teal-50 text-teal-800" : "text-slate-800 hover:bg-slate-50"}`}
                            data-testid={`mobile-lesson-${lesson.id}`}
                          >
                            {isDone
                              ? <CheckCircle2 className="w-4 h-4 text-[#0D9488] shrink-0" />
                              : <div className="w-4 h-4 rounded-full border-2 border-slate-300 shrink-0" />}
                            <span className="text-sm truncate flex-1">{lesson.title}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </SheetContent>
          </Sheet>

          {/* Sidebar: Syllabus — desktop-only.
              Mobile users tap the "Syllabus" trigger further down to open the drawer. */}
          <aside className="hidden lg:block border border-slate-200 bg-white rounded-xl p-4 h-fit lg:sticky lg:top-24" data-testid="playbook-sidebar">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-[#0D9488] mb-3 px-1">
              Syllabus
            </div>
            {sections.length === 0 ? (
              <p className="text-xs text-slate-500 px-1 py-2">
                {isStaff ? "No sections yet — add one below." : "No content yet."}
              </p>
            ) : (
              <div className="space-y-3">
                {sections.map((s, sIdx) => (
                  <div key={s.id} data-testid={`playbook-section-${s.id}`}>
                    <div className="flex items-center justify-between gap-1 px-1">
                      <div className="font-display font-black text-xs uppercase tracking-tight text-slate-900 truncate">
                        {s.title}
                      </div>
                      {isStaff && (
                        <div className="flex gap-0.5 shrink-0">
                          <button type="button" onClick={() => reorder("section", s.id, "up")} disabled={sIdx === 0} aria-disabled={sIdx === 0}
                            className="p-1 text-slate-500 hover:text-[#0D9488] disabled:opacity-30" title="Move section up"
                            data-testid={`section-up-${s.id}`}>
                            <ChevronUp className="w-3 h-3" />
                          </button>
                          <button type="button" onClick={() => reorder("section", s.id, "down")} disabled={sIdx === sections.length - 1} aria-disabled={sIdx === sections.length - 1}
                            className="p-1 text-slate-500 hover:text-[#0D9488] disabled:opacity-30" title="Move section down"
                            data-testid={`section-down-${s.id}`}>
                            <ChevronDown className="w-3 h-3" />
                          </button>
                          <button type="button" onClick={() => setLessonDialog({ open: true, sectionId: s.id, lesson: null })}
                            className="p-1 text-slate-500 hover:text-[#0D9488]" title="Add lesson"
                            data-testid={`section-add-lesson-${s.id}`}>
                            <Plus className="w-3 h-3" />
                          </button>
                          <button type="button" onClick={() => setSectionDialog({ open: true, section: s })}
                            className="p-1 text-slate-500 hover:text-[#0D9488]" title="Edit section"
                            data-testid={`section-edit-${s.id}`}>
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button type="button" onClick={() => setDeleteTarget({ type: "section", id: s.id, title: s.title })}
                            className="p-1 text-slate-500 hover:text-[#FF3B30]" title="Delete section"
                            data-testid={`section-delete-${s.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                    {s.lessons.length === 0 ? (
                      <p className="text-[10px] font-mono-num text-slate-400 uppercase tracking-widest px-1 py-1">no lessons</p>
                    ) : (
                      <ul className="mt-1 space-y-0.5">
                        {s.lessons.map((l, lIdx) => {
                          const done = completedIds.has(l.id);
                          const isActive = activeLessonId === l.id;
                          return (
                            <li key={l.id} className="flex items-center gap-0.5">
                              <button
                                type="button"
                                onClick={() => setActiveLessonId(l.id)}
                                className={`flex-1 min-w-0 text-left px-2 py-1.5 text-xs flex items-center justify-between gap-1 transition-colors ${
                                  isActive
                                    ? "bg-[#0D9488]/10 text-[#0D9488] border-l-2 border-[#0D9488]"
                                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50 border-l-2 border-transparent"
                                }`}
                                data-testid={`lesson-select-${l.id}`}
                              >
                                <span className="flex items-center gap-1.5 min-w-0">
                                  {done ? (
                                    <Check className="w-3 h-3 text-[#0D9488] shrink-0" />
                                  ) : (
                                    <span className="w-3 h-3 border border-slate-300 shrink-0" />
                                  )}
                                  <span className={`truncate ${done && !isActive ? "text-slate-500" : ""}`}>{l.title}</span>
                                </span>
                                <ChevronRight className={`w-3 h-3 shrink-0 ${isActive ? "opacity-100" : "opacity-0"}`} />
                              </button>
                              {isStaff && (
                                <div className="flex gap-0.5 shrink-0">
                                  <button type="button" onClick={() => reorder("lesson", l.id, "up")} disabled={lIdx === 0} aria-disabled={lIdx === 0}
                                    className="p-1 text-slate-400 hover:text-[#0D9488] disabled:opacity-30" title="Move up"
                                    data-testid={`lesson-up-${l.id}`}>
                                    <ChevronUp className="w-3 h-3" />
                                  </button>
                                  <button type="button" onClick={() => reorder("lesson", l.id, "down")} disabled={lIdx === s.lessons.length - 1} aria-disabled={lIdx === s.lessons.length - 1}
                                    className="p-1 text-slate-400 hover:text-[#0D9488] disabled:opacity-30" title="Move down"
                                    data-testid={`lesson-down-${l.id}`}>
                                    <ChevronDown className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
            {isStaff && (
              <button
                type="button"
                onClick={() => setSectionDialog({ open: true, section: null })}
                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 border border-dashed border-slate-200 hover:border-[#0D9488] text-slate-500 hover:text-[#0D9488] px-2 py-2 font-mono-num text-[10px] uppercase tracking-widest"
                data-testid="playbook-add-section"
              >
                <Plus className="w-3 h-3" /> Add Section
              </button>
            )}
          </aside>

          {/* Main content */}
          <main className="min-w-0">
            {activeLesson ? (
              <article className="border border-slate-200 bg-white p-8 sm:p-10 rounded-xl" data-testid="lesson-view">
                <div className="flex items-start justify-between gap-3 mb-8 pb-5 border-b border-slate-200">
                  <div className="min-w-0">
                    <div className="text-[11px] font-semibold uppercase tracking-widest text-[#0D9488] mb-2">
                      {activeLesson.section.title}
                    </div>
                    <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight" data-testid="lesson-title">
                      {activeLesson.lesson.title}
                    </h2>
                    {completionCounts[activeLesson.lesson.id] > 0 && (
                      <div className="mt-3 inline-flex items-center gap-1.5 bg-teal-50 border border-teal-200 rounded-full px-2.5 py-1 font-mono-num text-[10px] uppercase tracking-widest text-teal-700"
                        data-testid="lesson-completion-count">
                        <Users className="w-3 h-3" />
                        {completionCounts[activeLesson.lesson.id]} {completionCounts[activeLesson.lesson.id] === 1 ? "learner" : "learners"} completed
                      </div>
                    )}
                  </div>
                  {isStaff && (
                    <div className="flex gap-2 shrink-0">
                      <Button onClick={() => setLessonDialog({ open: true, sectionId: activeLesson.section.id, lesson: activeLesson.lesson })}
                        variant="outline" size="sm"
                        className="border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-800"
                        data-testid="lesson-edit">
                        <Pencil className="w-3 h-3 mr-1.5" /> Edit
                      </Button>
                      <Button onClick={() => setDeleteTarget({ type: "lesson", id: activeLesson.lesson.id, title: activeLesson.lesson.title })}
                        variant="outline" size="sm"
                        className="border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-600 hover:text-[#FF3B30]"
                        data-testid="lesson-delete">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
                {activeLesson.lesson.blocks.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">This lesson has no content yet.</p>
                ) : (playbook?.access_tier === "premium" && !playbook?.is_unlocked && !activeLesson.lesson.is_preview) ? (
                  <div className="relative">
                    <div className="space-y-5 pointer-events-none select-none blur-sm opacity-50" aria-hidden>
                      {activeLesson.lesson.blocks.map((block, i) => (
                        <LessonBlock key={i} block={block} lessonId={activeLesson.lesson.id} index={i} />
                      ))}
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center" data-testid="lesson-locked">
                      <div className="max-w-md text-center bg-white border border-[#0D9488] p-8">
                        <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-[#0D9488]">
                          <span className="text-white text-xl font-black">★</span>
                        </div>
                        <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-[#0D9488] mb-1">// Members Only</div>
                        <div className="font-display font-black uppercase text-2xl tracking-tight text-slate-900">This lesson is locked</div>
                        <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                          Preview lessons are open to everyone, but full content in this playbook is reserved for members.
                        </p>
                        {playbook?.price_usd ? (
                          <>
                            <button
                              type="button"
                              onClick={() => setUpgradePlaybookOnly(true)}
                              className="mt-5 w-full bg-[#0D9488] hover:bg-[#0F766E] text-white rounded-none h-11 px-5 uppercase tracking-widest text-xs font-semibold transition-colors"
                              data-testid="lesson-unlock-this-btn"
                            >
                              Unlock this playbook — ${Number(playbook.price_usd).toFixed(2)}
                            </button>
                            <button
                              type="button"
                              onClick={() => setUpgradeOpen(true)}
                              className="mt-2 w-full border border-slate-300 hover:border-[#0D9488] hover:text-[#0D9488] text-slate-800 rounded-none h-10 px-5 uppercase tracking-widest text-[10px] font-mono-num transition-colors"
                              data-testid="lesson-upgrade-btn"
                            >
                              or unlock ALL playbooks (Premium)
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setUpgradeOpen(true)}
                            className="mt-5 w-full bg-[#0D9488] hover:bg-[#0F766E] text-white rounded-none h-11 px-5 uppercase tracking-widest text-xs font-semibold transition-colors"
                            data-testid="lesson-upgrade-btn"
                          >
                            ★ Unlock Premium
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-5">
                    {activeLesson.lesson.blocks.map((block, i) => (
                      <LessonBlock key={i} block={block} lessonId={activeLesson.lesson.id} index={i} />
                    ))}
                  </div>
                )}

                {/* Lesson-level actions: mark complete + discuss */}
                <div className="mt-6 pt-4 border-t border-slate-200 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => toggleComplete(activeLesson.lesson.id)}
                    disabled={busyLesson === activeLesson.lesson.id}
                    className={`inline-flex items-center gap-2 h-9 px-3 font-mono-num text-[11px] uppercase tracking-widest font-black transition-colors ${
                      completedIds.has(activeLesson.lesson.id)
                        ? "bg-[#0D9488] text-white hover:bg-[#0F766E]"
                        : "border border-slate-200 text-slate-800 hover:border-[#0D9488] hover:text-[#0D9488]"
                    }`}
                    data-testid="lesson-complete-toggle"
                  >
                    {completedIds.has(activeLesson.lesson.id) ? (
                      <><CheckCircle2 className="w-3.5 h-3.5" /> Completed</>
                    ) : (
                      <><Check className="w-3.5 h-3.5" /> Mark as Complete</>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => openDiscussion(activeLesson.lesson.id)}
                    disabled={busyLesson === activeLesson.lesson.id}
                    className="inline-flex items-center gap-2 h-9 px-3 border border-slate-200 hover:border-[#0D9488] hover:text-[#0D9488] text-slate-800 font-mono-num text-[11px] uppercase tracking-widest font-black transition-colors"
                    data-testid="lesson-discuss"
                    title={activeLesson.lesson.linked_topic_id ? "Open discussion thread" : "Start a discussion thread for this lesson"}
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    {activeLesson.lesson.linked_topic_id ? "Open Discussion" : "Discuss This Lesson"}
                  </button>
                </div>

                {/* Prev / Next lesson pager — keeps the learner moving through the syllabus. */}
                {(prevLesson || nextLesson) && (
                  <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-3" data-testid="lesson-pager">
                    <button
                      type="button"
                      onClick={() => prevLesson && setActiveLessonId(prevLesson.id)}
                      disabled={!prevLesson}
                      className="inline-flex items-center gap-1.5 text-slate-600 hover:text-[#0D9488] disabled:opacity-30 disabled:hover:text-slate-600 text-xs font-mono-num uppercase tracking-widest transition-colors"
                      data-testid="lesson-prev"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Previous</span>
                      <span className="max-w-[10rem] truncate hidden md:inline text-slate-800 normal-case tracking-normal">
                        {prevLesson?.title}
                      </span>
                    </button>
                    <div className="text-[10px] font-mono-num uppercase tracking-widest text-slate-400 tabular-nums">
                      {activeIdx + 1} / {flatLessons.length}
                    </div>
                    <button
                      type="button"
                      onClick={() => nextLesson && setActiveLessonId(nextLesson.id)}
                      disabled={!nextLesson}
                      className="inline-flex items-center gap-1.5 text-slate-600 hover:text-[#0D9488] disabled:opacity-30 disabled:hover:text-slate-600 text-xs font-mono-num uppercase tracking-widest transition-colors"
                      data-testid="lesson-next"
                    >
                      <span className="max-w-[10rem] truncate hidden md:inline text-slate-800 normal-case tracking-normal">
                        {nextLesson?.title}
                      </span>
                      <span className="hidden sm:inline">Next</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </article>
            ) : (
              <div className="border border-dashed border-slate-200 p-12 text-center">
                <GraduationCap className="w-6 h-6 text-[#0D9488] mx-auto mb-2" />
                <div className="font-display font-black text-lg uppercase tracking-tight text-slate-900">
                  {sections.length === 0 ? "This playbook is empty" : "Pick a lesson from the sidebar"}
                </div>
                {isStaff && sections.length === 0 && (
                  <p className="text-slate-500 text-sm mt-2">Add a section, then add lessons inside it.</p>
                )}
              </div>
            )}
          </main>
        </div>
      </div>

      {isStaff && (
        <>
          <PlaybookDialog open={editPlaybookOpen} onOpenChange={setEditPlaybookOpen} playbook={playbook} onSave={savePlaybook} />
          <SectionDialog
            open={sectionDialog.open}
            onOpenChange={(o) => !o && setSectionDialog({ open: false, section: null })}
            section={sectionDialog.section}
            onSave={saveSection}
          />
          <LessonDialog
            open={lessonDialog.open}
            onOpenChange={(o) => !o && setLessonDialog({ open: false, sectionId: null, lesson: null })}
            lesson={lessonDialog.lesson}
            onSave={saveLesson}
          />
        </>
      )}

      <AlertDialog open={deletePlaybookOpen} onOpenChange={setDeletePlaybookOpen}>
        <AlertDialogContent className="bg-white border border-slate-200 text-slate-900 rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight text-xl font-black">Delete this playbook?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              This will permanently delete "{playbook.title}" and all of its sections + lessons. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-slate-200 hover:bg-slate-50 rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDeletePlaybook} className="bg-[#FF3B30] hover:bg-[#c9291f] text-white rounded-none">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-white border border-slate-200 text-slate-900 rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight text-xl font-black">
              Delete {deleteTarget?.type}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              This will delete "{deleteTarget?.title}"{deleteTarget?.type === "section" ? " and all lessons inside it" : ""}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-slate-200 hover:bg-slate-50 rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#FF3B30] hover:bg-[#c9291f] text-white rounded-none">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PayPalCheckoutDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} />
      <PayPalCheckoutDialog
        open={upgradePlaybookOnly}
        onOpenChange={setUpgradePlaybookOnly}
        playbookId={playbook?.id}
        priceOverride={playbook?.price_usd}
        productName={playbook?.title ? `${playbook.title} — one-time unlock` : null}
        onUnlocked={() => load()}
      />
    </AppLayout>
  );
}
